import React, { useState, useCallback, useEffect } from "react";
import { useI18n } from "../i18n";

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Componente que solo muestra las tablas de poblaciones cercanas
 * con información de cargadores en tooltip
 */
export default function NearbyMunicipalitiesTables({ 
  nearbyData, 
  onSelectMunicipality,
  selectedMunicipalities = [],
  filters
}) {
  const { t } = useI18n();
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, data: null, loading: false, municipio: null });
  const [pinnedTooltips, setPinnedTooltips] = useState({});
  const [chargersCache, setChargersCache] = useState({});
  const [dragState, setDragState] = useState(null);
  
  // Helper para generar id único de municipio
  const getMunicipalityId = (m) => `${m.lat}-${m.lng}`;
  
  const getTooltipPosition = (rect) => ({
    x: Math.min(rect.right + 10, window.innerWidth - 320),
    y: Math.max(rect.top, 10)
  });

  const handlePinnedDragStart = (event, tooltipId) => {
    event.preventDefault();
    const currentTooltip = pinnedTooltips[tooltipId];
    if (!currentTooltip) return;

    // Evita que quede visible un tooltip de hover "debajo" del popup fijado.
    setTooltip({ visible: false, x: 0, y: 0, data: null, loading: false, municipio: null });

    setDragState({
      id: tooltipId,
      offsetX: event.clientX - currentTooltip.x,
      offsetY: event.clientY - currentTooltip.y
    });
  };

  useEffect(() => {
    if (!dragState) return;

    const handleMouseMove = (event) => {
      const nextX = Math.max(0, Math.min(event.clientX - dragState.offsetX, window.innerWidth - 320));
      const nextY = Math.max(0, Math.min(event.clientY - dragState.offsetY, window.innerHeight - 60));

      setPinnedTooltips((prev) => {
        if (!prev[dragState.id]) return prev;
        return {
          ...prev,
          [dragState.id]: {
            ...prev[dragState.id],
            x: nextX,
            y: nextY
          }
        };
      });
    };

    const handleMouseUp = () => {
      setDragState(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragState]);

  // Handler para click en fila: toggle de selección y tooltip fijado
  const handleRowClick = async (event, m, type) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const position = getTooltipPosition(rect);
    const municipalityWithId = {
      ...m,
      type,
      id: getMunicipalityId(m)
    };

    // Al fijar/deseleccionar, cerramos el tooltip hover para no duplicar ventanas.
    setTooltip({ visible: false, x: 0, y: 0, data: null, loading: false, municipio: null });

    onSelectMunicipality?.(municipalityWithId);

    const isAlreadyPinned = !!pinnedTooltips[municipalityWithId.id];
    if (isAlreadyPinned) {
      setPinnedTooltips((prev) => {
        const { [municipalityWithId.id]: _removed, ...rest } = prev;
        return rest;
      });
      return;
    }

    setPinnedTooltips((prev) => ({
      ...prev,
      [municipalityWithId.id]: {
        id: municipalityWithId.id,
        municipio: municipalityWithId,
        x: position.x,
        y: position.y,
        data: null,
        loading: true
      }
    }));

    const chargers = await loadChargers(municipalityWithId);
    setPinnedTooltips((prev) => {
      if (!prev[municipalityWithId.id]) return prev;
      return {
        ...prev,
        [municipalityWithId.id]: {
          ...prev[municipalityWithId.id],
          data: chargers,
          loading: false
        }
      };
    });
  };
  
  // Comprobar si un municipio está seleccionado
  const isSelected = (m) => {
    const id = getMunicipalityId(m);
    return selectedMunicipalities.some((selected) => selected.id === id);
  };

  // Cargar cargadores para una población
  const loadChargers = useCallback(async (municipio) => {
    const filterKey = JSON.stringify(filters || {});
    const cacheKey = `${municipio.name}-${municipio.provincia}-${filterKey}`;
    
    if (chargersCache[cacheKey]) {
      return chargersCache[cacheKey];
    }
    
    try {
      const params = new URLSearchParams({
        municipality: municipio.name,
        distance: '10',
      });
      if (filters?.currentTypes?.length) params.set('currentTypes', filters.currentTypes.join(','));
      if (filters?.connectors?.length) params.set('connectors', filters.connectors.join(','));
      if (filters?.powerRanges?.length) params.set('powerRanges', filters.powerRanges.join(','));
      const response = await fetch(
        `${API_URL}/api/chargers/by-municipality?${params}`
      );
      const data = await response.json();
      
      if (data.success && data.data) {
        const chargers = data.data.slice(0, 10); // Máximo 10 para el tooltip
        setChargersCache(prev => ({ ...prev, [cacheKey]: chargers }));
        return chargers;
      }
      return [];
    } catch (err) {
      console.error('Error cargando cargadores:', err);
      return [];
    }
  }, [chargersCache, filters]);

  // Handler para mouse enter en fila
  const handleMouseEnter = async (event, municipio) => {
    const rect = event.currentTarget.getBoundingClientRect();
    
    const id = getMunicipalityId(municipio);
    if (pinnedTooltips[id]) return;

    setTooltip({
      visible: true,
      x: getTooltipPosition(rect).x,
      y: getTooltipPosition(rect).y,
      data: null,
      loading: true,
      municipio
    });
    
    const chargers = await loadChargers(municipio);
    
    setTooltip(prev => ({
      ...prev,
      data: chargers,
      loading: false
    }));
  };

  // Handler para mouse leave
  const handleMouseLeave = (municipio) => {
    const id = getMunicipalityId(municipio);
    if (pinnedTooltips[id]) return;
    setTooltip({ visible: false, x: 0, y: 0, data: null, loading: false, municipio: null });
  };

  const renderTooltipContent = (tooltipState) => {
    if (tooltipState.loading) {
      return <p style={{ color: "#666", fontSize: "0.8rem" }}>{t("chargers.loadingTooltip")}</p>;
    }

    if (tooltipState.data && tooltipState.data.length > 0) {
      return (
        <div>
          <p style={{ fontSize: "0.75rem", color: "#059669", marginBottom: "8px" }}>
            {tooltipState.data.length} {t("chargers.foundNearby", { count: tooltipState.data.length })}
          </p>
          <ul style={{ margin: 0, padding: "0 0 0 16px", fontSize: "0.75rem" }}>
            {tooltipState.data.slice(0, 10).map((charger, idx) => (
              <li key={idx} style={{ marginBottom: "4px" }}>
                <strong>{charger.operator || t('chargers.noOperator')}</strong>
                {charger.maxPower && <span> - {charger.maxPower}kW</span>}
                <br />
                <span style={{ color: "#666" }}>
                  {charger.address?.street || charger.address?.city || t('chargers.noAddress')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      );
    }

    return (
      <p style={{ color: "#999", fontSize: "0.8rem" }}>
        {t("chargers.noChargers")}
      </p>
    );
  };

  if (!nearbyData || !Array.isArray(nearbyData.points) || nearbyData.points.length === 0) {
    return (
      <p style={{ textAlign: "center", color: "#666", padding: "1rem" }}>
        {t("nearby.noData")}
      </p>
    );
  }

  return (
    <div style={{ overflowX: "auto", position: "relative" }}>
      {/* Tooltip flotante al pasar por encima */}
      {tooltip.visible && (
        <div style={{
          position: "fixed",
          left: tooltip.x,
          top: tooltip.y,
          background: "white",
          border: "1px solid #ddd",
          borderRadius: "8px",
          padding: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 1000,
          maxWidth: "300px",
          maxHeight: "300px",
          overflowY: "auto"
        }}>
          <h5 style={{ margin: "0 0 8px 0", color: "#2563eb", fontSize: "0.9rem" }}>
            ⚡ {t("chargers.chargersIn", { city: tooltip.municipio?.name })}
          </h5>
          {renderTooltipContent(tooltip)}
        </div>
      )}

      {/* Tooltips fijados por selección (pueden coexistir varios) */}
      {Object.values(pinnedTooltips).map((pinned) => (
        <div
          key={pinned.id}
          style={{
            position: "fixed",
            left: pinned.x,
            top: pinned.y,
            background: "white",
            border: "2px solid #2563eb",
            borderRadius: "8px",
            padding: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            zIndex: 1100,
            maxWidth: "300px",
            maxHeight: "320px",
            overflowY: "auto"
          }}
        >
          <div
            onMouseDown={(event) => handlePinnedDragStart(event, pinned.id)}
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "8px",
              alignItems: "center",
              cursor: "move",
              userSelect: "none",
              borderBottom: "1px solid #e5e7eb",
              margin: "-4px -4px 8px -4px",
              padding: "4px"
            }}
            title="Arrastra para mover"
          >
            <h5 style={{ margin: "0 0 8px 0", color: "#2563eb", fontSize: "0.9rem" }}>
              ⚡ {t("chargers.chargersIn", { city: pinned.municipio?.name })}
            </h5>
            <button
              type="button"
              onMouseDown={(event) => event.stopPropagation()}
              onClick={() => {
                setPinnedTooltips((prev) => {
                  const { [pinned.id]: _removed, ...rest } = prev;
                  return rest;
                });
                onSelectMunicipality?.(pinned.municipio);
              }}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                color: "#666",
                fontSize: "1rem",
                lineHeight: 1
              }}
              title="Cerrar"
            >
              ×
            </button>
          </div>
          {renderTooltipContent(pinned)}
        </div>
      ))}

      {nearbyData.points.map((point, idx) => (
        <div key={idx} style={{ marginBottom: "1.5rem" }}>
          <h4 style={{ 
            background: "linear-gradient(135deg, #dbeafe, #ede9fe)", 
            padding: "0.6rem 1rem", 
            borderRadius: "6px",
            marginBottom: "0.5rem",
            fontSize: "0.95rem",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}>
            <span style={{ fontSize: "1.2rem" }}>⚡</span>
            {t("chargers.stationsAtKm", { km: point.kmPoint })}
          </h4>
          
          <table style={{ 
            width: "100%", 
            borderCollapse: "collapse", 
            fontSize: "0.85rem",
            border: "1px solid #e5e7eb"
          }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                <th style={thStyle}>{t("nearby.columnType")}</th>
                <th style={thStyle}>{t("nearby.columnCity")}</th>
                <th style={thStyle}>{t("nearby.columnProvince")}</th>
                <th style={thStyle}>{t("nearby.columnKmOrigin")}</th>
                <th style={thStyle}>{t("nearby.columnDeviation")}</th>
                <th style={thStyle}>⚡ {t("chargers.connectors")}</th>
              </tr>
            </thead>
            <tbody>
              {point.before.length === 0 && point.after.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ ...tdStyle, textAlign: "center", color: "#666" }}>
                    {t("nearby.noNearby")}
                  </td>
                </tr>
              )}
              
              {point.before.map((m, i) => (
                <tr 
                  key={`before-${i}`} 
                  style={{ 
                    background: isSelected(m) ? "#f59e0b" : "#fef3c7",
                    cursor: "pointer",
                    transition: "background-color 0.2s"
                  }}
                  onClick={(e) => handleRowClick(e, m, "before")}
                  onMouseEnter={(e) => handleMouseEnter(e, m)}
                  onMouseLeave={() => handleMouseLeave(m)}
                >
                  <td style={tdStyle}>
                    <span style={{ color: "#d97706", fontWeight: "bold" }}>{t("nearby.before")}</span>
                  </td>
                  <td style={tdStyle}><strong>{m.name}</strong></td>
                  <td style={tdStyle}>{m.provincia}</td>
                  <td style={tdStyle}>{m.kmFromOrigin} km</td>
                  <td style={tdStyle}>{m.distanceToRoute} km</td>
                  <td style={{ ...tdStyle, textAlign: "center", cursor: "help", color: "#2563eb", fontWeight: "600" }}>
                    🔌 {t("chargers.chargersIn", { city: "" }).trim()}
                  </td>
                </tr>
              ))}
              
              <tr style={{ background: "linear-gradient(90deg, #dbeafe, #c7d2fe)" }}>
                <td colSpan={6} style={{ ...tdStyle, textAlign: "center", fontWeight: "bold", color: "#2563eb", fontSize: "0.9rem" }}>
                  ⚡ {t("nearby.pointLabel", { km: point.kmPoint })}
                </td>
              </tr>
              
              {point.after.map((m, i) => (
                <tr 
                  key={`after-${i}`} 
                  style={{ 
                    background: isSelected(m) ? "#8b5cf6" : "#d1fae5",
                    cursor: "pointer",
                    transition: "background-color 0.2s"
                  }}
                  onClick={(e) => handleRowClick(e, m, "after")}
                  onMouseEnter={(e) => handleMouseEnter(e, m)}
                  onMouseLeave={() => handleMouseLeave(m)}
                >
                  <td style={tdStyle}>
                    <span style={{ color: "#059669", fontWeight: "bold" }}>{t("nearby.after")}</span>
                  </td>
                  <td style={tdStyle}><strong>{m.name}</strong></td>
                  <td style={tdStyle}>{m.provincia}</td>
                  <td style={tdStyle}>{m.kmFromOrigin} km</td>
                  <td style={tdStyle}>{m.distanceToRoute} km</td>
                  <td style={{ ...tdStyle, textAlign: "center", cursor: "help", color: "#2563eb", fontWeight: "600" }}>
                    🔌 {t("chargers.chargersIn", { city: "" }).trim()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

const thStyle = {
  padding: "0.5rem",
  textAlign: "left",
  borderBottom: "2px solid #e5e7eb",
  fontWeight: "600",
};

const tdStyle = {
  padding: "0.5rem",
  borderBottom: "1px solid #e5e7eb",
};
